import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Factory } from 'lucide-react';
import ClientsVendorsSection from '../components/ClientsVendorsSection';

const HomePage = () => {
  const bespokeCategories = [
    {
      title: 'Corporate Uniforms',
      desc: 'Professional attire for modern workplaces',
      image: 'https://images.unsplash.com/photo-1758520144424-2e04f4ad7f60?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      title: 'School Uniforms',
      desc: 'Durable and comfortable for students',
      image: 'https://images.unsplash.com/photo-1758518730523-c9f6336ebdae?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      title: 'Industrial Wear',
      desc: 'Safety-first workwear solutions',
      image: 'https://images.unsplash.com/photo-1684259499086-93cb3e555803?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      title: 'Sports Teams',
      desc: 'Custom jerseys and team apparel',
      image: 'https://images.unsplash.com/photo-1752778598489-ae34ac74ab34?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
  ];

  const processSteps = [
    { num: '01', title: 'Consultation', desc: 'Understanding your needs' },
    { num: '02', title: 'Design & Sampling', desc: 'Creating your vision' },
    { num: '03', title: 'Manufacturing', desc: 'Precision production' },
    { num: '04', title: 'Quality Control', desc: 'Rigorous testing' },
    { num: '05', title: 'Delivery', desc: 'Timely dispatch' },
  ];

  const collections = [
    { name: 'Sportswear', link: '/shop?category=sportswear', image: 'https://images.unsplash.com/photo-1689395218980-2b3ef140d77e?crop=entropy&cs=srgb&fm=jpg&q=85' },
    { name: 'Streetwear', link: '/shop?category=streetwear', image: 'https://images.unsplash.com/photo-1761646062304-096a18cc4233?crop=entropy&cs=srgb&fm=jpg&q=85' },
    { name: 'Comfort Wear', link: '/shop?category=comfort-wear', image: 'https://images.unsplash.com/photo-1745240261519-0b988d54d098?crop=entropy&cs=srgb&fm=jpg&q=85' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section - Optimized & Product-First */}
      <section className="pt-32 pb-16 md:pb-24" data-testid="hero-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold text-brand-pearl leading-tight mb-6" data-testid="hero-heading">
                Bespoke Uniform Manufacturing Built for Scale
              </h1>
              <p className="text-lg text-brand-walnut leading-relaxed mb-8 max-w-xl">
                Custom uniforms and premium apparel manufacturing for offices, schools, institutions, and teams.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-quote"
                  className="inline-flex items-center justify-center px-8 py-4 bg-brand-pearl text-brand-ivory font-semibold text-lg rounded-sm hover:bg-brand-walnut transition-colors shadow-lg hover:shadow-xl"
                >
                  Get a Quote
                  <ArrowRight className="ml-2 w-6 h-6" />
                </Link>
                <Link
                  to="/shop"
                  data-testid="hero-cta-shop"
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-brand-pearl text-brand-pearl font-semibold text-lg rounded-sm hover:bg-brand-pearl hover:text-brand-ivory transition-colors"
                >
                  Explore Shop
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-sm overflow-hidden border-2 border-brand-pearl/20 shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1758270804188-8ca0b6d254bc?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Manufacturing Excellence"
                  className="w-full h-auto object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bespoke Overview - Optimized */}
      <section className="py-16 md:py-24 bg-brand-timberwolf" data-testid="bespoke-overview-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-heading font-bold text-brand-pearl mb-4" data-testid="bespoke-heading">
              Bespoke Manufacturing Excellence
            </h2>
            <p className="text-lg text-brand-walnut max-w-2xl mx-auto">
              Tailored solutions for every industry. From concept to delivery, we manufacture uniforms that embody your brand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {bespokeCategories.map((category, idx) => (
              <div
                key={idx}
                className="bg-brand-ivory rounded-sm border border-brand-pearl/10 overflow-hidden hover:shadow-lg transition-shadow duration-300"
                data-testid={`bespoke-category-${idx}`}
              >
                <div className="overflow-hidden">
                  <img src={category.image} alt={category.title} className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-heading font-semibold text-brand-pearl mb-2">
                    {category.title}
                  </h3>
                  <p className="text-sm text-brand-walnut">{category.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/bespoke"
              data-testid="view-all-bespoke-btn"
              className="inline-flex items-center px-8 py-3 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors"
            >
              View All Solutions
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="section-spacing" data-testid="process-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brand-pearl mb-4" data-testid="process-heading">
              How We Work
            </h2>
            <p className="text-base sm:text-lg text-brand-walnut max-w-2xl mx-auto">
              A proven 5-step process that ensures quality, consistency, and scale.
            </p>
          </div>

          <div className="relative">
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-brand-pearl/10 -translate-y-1/2 hidden lg:block" />
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 relative">
              {processSteps.map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="text-center"
                  data-testid={`process-step-${idx}`}
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-pearl text-brand-ivory flex items-center justify-center font-mono text-lg font-bold">
                    {step.num}
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-brand-pearl mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-brand-walnut">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/process"
              data-testid="learn-more-process-btn"
              className="inline-flex items-center px-8 py-3 border-2 border-brand-pearl text-brand-pearl font-medium rounded-sm hover:bg-brand-pearl hover:text-brand-ivory transition-colors"
            >
              Learn More About Our Process
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Shop Preview */}
      <section className="section-spacing bg-brand-timberwolf" data-testid="shop-preview-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brand-pearl mb-4" data-testid="shop-heading">
              Ready-to-Wear Collections
            </h2>
            <p className="text-base sm:text-lg text-brand-walnut max-w-2xl mx-auto">
              Premium apparel designed for performance, style, and comfort.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {collections.map((collection, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="card-hover"
                data-testid={`collection-${idx}`}
              >
                <Link to={collection.link} className="block">
                  <div className="bg-brand-ivory rounded-sm border border-brand-pearl/10 overflow-hidden">
                    <div className="parallax-img">
                      <img src={collection.image} alt={collection.name} className="w-full h-64 object-cover" />
                    </div>
                    <div className="p-6 text-center">
                      <h3 className="text-2xl font-heading font-semibold text-brand-pearl">
                        {collection.name}
                      </h3>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/shop"
              data-testid="browse-shop-btn"
              className="inline-flex items-center px-8 py-3 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors"
            >
              Browse Full Collection
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="section-spacing" data-testid="trust-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <div data-testid="trust-indicator-0">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-timberwolf flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-brand-pearl" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-brand-pearl mb-2">15+ Years</h3>
              <p className="text-brand-walnut">Manufacturing Excellence</p>
            </div>
            <div data-testid="trust-indicator-1">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-timberwolf flex items-center justify-center">
                <Factory className="w-8 h-8 text-brand-pearl" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-brand-pearl mb-2">500+ Clients</h3>
              <p className="text-brand-walnut">Across India</p>
            </div>
            <div data-testid="trust-indicator-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-timberwolf flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-brand-pearl" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-brand-pearl mb-2">100% Quality</h3>
              <p className="text-brand-walnut">Guaranteed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Clients & Vendors */}
      <ClientsVendorsSection />
    </div>
  );
};

export default HomePage;